import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FadeUp from '../components/common/FadeUp';
import {
  ABOUT_STATS,
  calcWidgetMetrics,
  DEFAULT_WIDGET,
  FEATURED_TOOLS,
  FOR_WHOM_CARDS,
  formatWidgetMoney,
  FUNNEL_STEPS,
  NOT_FOR_CHIPS,
  PLAN_ITEMS,
  PROBLEM_ITEMS,
} from '../components/landing-v2/landingV2Data';
import portrait from '../assets/about/nadya-chub.jpg';
import useDocumentTitle from '../hooks/useDocumentTitle';
import '../components/landing-v2/landing-v2-fonts';
import '../components/landing-v2/landing-v2.css';

const FunnelIcon = ({ variant }) => {
  if (variant === 'chart') {
    return (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M3 15l4-5 3 3 3-4 4 6"
          stroke="var(--accent)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M2 4h16" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }

  if (variant === 'up') {
    return (
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M10 3v14M4 9l6-6 6 6"
          stroke="rgba(200,75,47,0.9)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="4" y="2" width="12" height="16" rx="2" stroke="var(--green)" strokeWidth="1.5" />
      <path d="M7 7h6M7 11h4" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
};

const ToolsLandingPage2 = () => {
  useDocumentTitle('marketDir — директор по маркетингу для малого бизнеса');
  const [widget, setWidget] = useState(DEFAULT_WIDGET);

  const metrics = useMemo(() => calcWidgetMetrics(widget), [widget]);

  const updateWidget = (field) => (event) => {
    setWidget((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  return (
    <div className="landing-v2">
      <div className="lv2-wrap">
        <FadeUp as="section" className="lv2-hero" delay={0}>
          <div>
            <div className="lv2-hero-eyebrow">
              <span className="lv2-hero-eyebrow-line" />
              Директор по маркетингу для малого бизнеса
            </div>
            <h1 className="lv2-hero-h1">
              <span className="lv2-hero-h1-line">Считайте маркетинг,</span>
              <span className="lv2-hero-h1-line">
                а не <em>угадывайте</em>
              </span>
            </h1>
            <p className="lv2-hero-lead">
              Введите цифры своего бизнеса: узнайте, где теряете деньги, как выглядит ваша точка Б и что
              конкретно делать для её достижения.
            </p>
            <div className="lv2-hero-actions">
              <Link to="/diagnostic" className="lv2-btn-primary">
                Пройти диагностику →
              </Link>
              <a href="#how" className="lv2-btn-secondary">
                Как это работает
              </a>
            </div>
            <p className="lv2-hero-trust">
              <span>Бесплатная диагностика</span>
              <span className="lv2-trust-dot">·</span>
              <span>Без регистрации</span>
              <span className="lv2-trust-dot">·</span>
              <span>5 минут</span>
            </p>
          </div>

          <div className="lv2-hero-widget" id="widget">
            <div className="lv2-widget-label">Быстрая диагностика точки А</div>
            <div className="lv2-widget-field">
              <label htmlFor="lv2-revenue">Выручка в месяц, ₸</label>
              <input
                id="lv2-revenue"
                type="number"
                value={widget.revenue}
                onChange={updateWidget('revenue')}
              />
            </div>
            <div className="lv2-widget-field">
              <label htmlFor="lv2-ads">Расходы на рекламу, ₸</label>
              <input id="lv2-ads" type="number" value={widget.ads} onChange={updateWidget('ads')} />
            </div>
            <div className="lv2-widget-field">
              <label htmlFor="lv2-check">Средний чек, ₸</label>
              <input id="lv2-check" type="number" value={widget.check} onChange={updateWidget('check')} />
            </div>
            <div className="lv2-widget-sep" />
            <div className="lv2-widget-result-label">Потенциал роста выручки</div>
            <div className="lv2-widget-result-big">+{formatWidgetMoney(metrics.potential)}</div>
            <div className="lv2-widget-result-sub">при оптимизации ключевых рычагов роста</div>
            <div className="lv2-widget-mini-grid">
              <div className="lv2-widget-mini">
                <div className="lv2-widget-mini-label">Доля рекламы в выручке</div>
                <div className="lv2-widget-mini-val">{metrics.share}</div>
              </div>
              <div className="lv2-widget-mini">
                <div className="lv2-widget-mini-label">Клиентов в месяц</div>
                <div className="lv2-widget-mini-val">{metrics.clients}</div>
              </div>
            </div>
            <a href="#how" className="lv2-widget-cta">
              Получить полный план роста →
            </a>
            <div className="lv2-widget-hint">Полный план — 3 сценария + маркетинг за 25 000 ₸</div>
          </div>
        </FadeUp>
      </div>

      <div className="lv2-problem-strip">
        <div className="lv2-problem-inner">
          {PROBLEM_ITEMS.map((item) => (
            <div key={item.question} className="lv2-problem-item">
              <div className="lv2-problem-q">{item.question}</div>
              <p className="lv2-problem-a">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="lv2-wrap">
        <section className="lv2-section" id="how">
          <FadeUp delay={0}>
            <div className="lv2-section-eyebrow">
              <span className="lv2-section-eyebrow-line" />
              Как работает
            </div>
            <h2 className="lv2-section-title">
              От цифр — к <em>конкретным действиям</em>
            </h2>
            <p className="lv2-section-lead">
              Три шага от диагностики текущего состояния до маркетингового плана на 6 месяцев
            </p>
          </FadeUp>
          <FadeUp className="lv2-funnel" delay={80}>
            {FUNNEL_STEPS.map((step, index) => (
              <React.Fragment key={step.num}>
                {index > 0 ? <div className="lv2-funnel-arrow">→</div> : null}
                <div
                  className={`lv2-funnel-step${step.highlight ? ' lv2-funnel-step--highlight' : ''}`}
                >
                  <div className="lv2-fs-num">{step.num}</div>
                  <div className="lv2-fs-icon">
                    <FunnelIcon variant={index === 0 ? 'chart' : index === 1 ? 'up' : 'doc'} />
                  </div>
                  <div className="lv2-fs-title">{step.title}</div>
                  <div className="lv2-fs-desc">{step.description}</div>
                  <div className={`lv2-fs-badge lv2-fs-badge--${step.badgeClass}`}>{step.badge}</div>
                </div>
              </React.Fragment>
            ))}
          </FadeUp>
        </section>
      </div>

      <div className="lv2-plan-bg" id="plan">
        <div className="lv2-wrap">
          <div className="lv2-plan-grid">
            <FadeUp delay={0} className="lv2-plan-copy">
              <div className="lv2-section-eyebrow">
                <span className="lv2-section-eyebrow-line" />
                Что входит в план роста
              </div>
              <h2 className="lv2-section-title lv2-plan-title">
                Директор по маркетингу —
                <br />
                всего за <em>25 000 ₸</em>
              </h2>
              <p className="lv2-plan-intro">
                Не шаблонный совет. Конкретный маршрут под ваши цифры: математическая точность и экспертный
                взгляд.
              </p>
              <div className="lv2-plan-includes">
                {PLAN_ITEMS.map((item) => (
                  <div key={item.num} className="lv2-plan-item">
                    <div className="lv2-plan-item-num">{item.num}</div>
                    <p className="lv2-plan-item-text">
                      <strong>{item.title}</strong> — {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={80}>
              <div className="lv2-price-card">
                <div className="lv2-pc-eyebrow">MarketDir Online — план роста</div>
                <h3 className="lv2-pc-title">
                  Математическая точность +
                  <br />
                  <em>экспертный взгляд</em>
                </h3>
                <p className="lv2-pc-sub">
                  Калькуляторы дают данные. Директор даёт интерпретацию. Вы получаете маршрут.
                </p>
                <div className="lv2-pc-price">25 000 ₸</div>
                <div className="lv2-pc-price-sub">полный план роста на 6 месяцев</div>
                <Link to="/diagnostic" className="lv2-pc-btn">
                  Начать диагностику →
                </Link>
                <div className="lv2-pc-note">Сначала бесплатная диагностика — потом решаете</div>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>

      <div className="lv2-wrap">
        <FadeUp as="section" className="lv2-section" delay={0}>
          <div className="lv2-section-eyebrow">
            <span className="lv2-section-eyebrow-line" />
            Для кого
          </div>
          <h2 className="lv2-section-title">
            Малый бизнес, которому
            <br />
            нужна <em>система</em>
          </h2>
          <p className="lv2-section-lead">
            Не советы из интернета. Не дорогой директор в штат. Конкретный маршрут под ваш бизнес.
          </p>
          <div className="lv2-fw-grid">
            {FOR_WHOM_CARDS.map((card) => (
              <div key={card.title} className="lv2-fw-card">
                <div className="lv2-fw-icon">{card.icon}</div>
                <div className="lv2-fw-title">{card.title}</div>
                <p className="lv2-fw-desc">{card.description}</p>
              </div>
            ))}
          </div>
          <div className="lv2-not-for">
            <div className="lv2-not-for-title">Не подойдёт если</div>
            <div className="lv2-not-for-chips">
              {NOT_FOR_CHIPS.map((chip) => (
                <span key={chip} className="lv2-nf-chip">
                  {chip}
                </span>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>

      <div className="lv2-tools-bg" id="tools">
        <div className="lv2-wrap">
          <FadeUp delay={0}>
            <div className="lv2-section-eyebrow">
              <span className="lv2-section-eyebrow-line" />
              Бесплатные инструменты
            </div>
            <h2 className="lv2-section-title">
              Считайте сами —
              <br />
              пока не готовы к полной <em>диагностике</em>
            </h2>
            <p className="lv2-section-lead lv2-section-lead--tight">
              15+ бесплатных калькуляторов и шаблонов. Без регистрации. Результат за 2 минуты.
            </p>
          </FadeUp>
          <FadeUp className="lv2-tools-grid" delay={80}>
            {FEATURED_TOOLS.map((tool) => (
              <Link key={tool.to} to={tool.to} className="lv2-tc">
                <div className="lv2-tc-cat">{tool.category}</div>
                <div className="lv2-tc-name">{tool.name}</div>
                <p className="lv2-tc-desc">{tool.description}</p>
              </Link>
            ))}
            <Link to="/tools" className="lv2-tc lv2-tc-more">
              <div className="lv2-tc-more-icon">→</div>
              <div className="lv2-tc-name">Все инструменты</div>
              <p className="lv2-tc-desc">15+ калькуляторов</p>
            </Link>
          </FadeUp>
          <FadeUp className="lv2-tools-cta-row" delay={120}>
            <p className="lv2-tools-note">
              Инструменты дают цифры. <strong>Диагностика объясняет, что с ними делать.</strong>
            </p>
            <Link to="/diagnostic" className="lv2-btn-primary">
              Пройти диагностику →
            </Link>
          </FadeUp>
        </div>
      </div>

      <div className="lv2-wrap">
        <FadeUp className="lv2-about-strip" delay={0}>
          <div>
            <div className="lv2-section-eyebrow">
              <span className="lv2-section-eyebrow-line" />
              Кто за этим стоит
            </div>
            <h2 className="lv2-section-title">
              marketDir —
              <br />
              <em>это я</em>
            </h2>
            <p className="lv2-body-text">
              <strong>Надежда Чуб</strong> — директор по маркетингу с 12+ годами опыта. Прошла путь от PPC-специалиста до руководителя digital-направления в корпорации.
              Управляла бюджетами в сотни тысяч долларов и командами в пяти странах.
            </p>
            <p className="lv2-body-text lv2-body-text--spaced">
              marketDir создан, потому что малому бизнесу тоже нужен директор по маркетингу — но не всегда
              его можно нанять.
            </p>
            <div className="lv2-about-stats">
              {ABOUT_STATS.map((stat) => (
                <div key={stat.label} className="lv2-as">
                  <div className="lv2-as-num">{stat.num}</div>
                  <div className="lv2-as-label">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="lv2-btn-secondary">
              Узнать больше →
            </Link>
          </div>
          <div className="lv2-about-photo">
            <img src={portrait} alt="Надежда Чуб" />
          </div>
        </FadeUp>
      </div>

      <div className="lv2-cta-band">
        <div className="lv2-cta-inner">
          <div>
            <h2 className="lv2-cta-h">
              Узнайте свою точку А — получите маршрут до точки <em>Б</em>.
            </h2>
            <div className="lv2-cta-sub">Бесплатная диагностика — 5 минут. Полный план роста — 25 000 ₸.</div>
          </div>
          <div className="lv2-cta-btns">
            <Link to="/diagnostic" className="lv2-btn-white">
              Начать диагностику →
            </Link>
            <Link to="/about" className="lv2-btn-ghost">
              Обо мне
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolsLandingPage2;
